<template>
  <Dialog :showHeader="false" :visible.sync="showDialog" :modal="true" :containerStyle="{width: '50vw'}">
    <div class="flex flex-col py-6 px-4 h-auto" v-if="ticket">
      <h2 class="text-secondary font-bold text-3xl mb-1 leading-none text-center">
        Service Ticket Created
      </h2>
      <p class="text-center text-gray-600 mb-6">
        Logged {{ createdOn }} by {{ ticket.created_by }}
      </p>

      <div class="flex flex-wrap -mx-2 mb-2">
        <div class="w-full lg:w-1/2 px-2 mb-4">
          <label class="uppercase font-semibold text-sm">Ticket Number</label>
          <p
              class="bg-orange-300 text-orange-900 rounded text-center text-3xl font-bold py-3 px-4"
              data-testid="created-ticket-number"
          >
            {{ ticket.ticket_number }}
          </p>
        </div>
        <div class="w-full lg:w-1/2 px-2 mb-4" v-if="ticket.warranty_number">
          <label class="uppercase font-semibold text-sm">Warranty Number</label>
          <p
              class="bg-orange-300 text-orange-900 rounded text-center text-3xl font-bold py-3 px-4"
              data-testid="created-warranty-number"
          >
            {{ ticket.warranty_number }}
          </p>
        </div>
      </div>

      <div class="bg-gray-100 rounded p-4 mb-4">
        <div class="flex flex-wrap -mx-2">
          <div
              v-for="detail in details"
              :key="detail.label"
              class="w-full lg:w-1/2 px-2 mb-3"
          >
            <label class="uppercase font-semibold text-xs text-gray-600">{{ detail.label }}</label>
            <p class="text-secondary" :data-testid="'created-' + detail.key">{{ detail.value }}</p>
          </div>
        </div>
      </div>

      <div class="mb-4" v-if="ticket.items_for_return">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mb-2">
          Items for Return
        </h3>
        <p class="bg-gray-100 rounded p-3 whitespace-pre-wrap" data-testid="created-items-for-return">
          {{ ticket.items_for_return_notes || "(No details added)" }}
        </p>
      </div>

      <div class="mb-4" v-if="ticket.warranty_reason">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mb-2">
          Reason Warranty Agreed
        </h3>
        <p class="bg-gray-100 rounded p-3 whitespace-pre-wrap" data-testid="created-warranty-reason">
          {{ ticket.warranty_reason }}
        </p>
      </div>

      <div class="flex justify-center w-full mt-2 -mx-2">
        <button
            type="button"
            @click="copyDetails"
            data-testid="created-copy"
            class="w-1/2 mx-2 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-secondary py-2 px-4 font-semibold rounded border border-gray-400 hover:border-gray-500 hover:shadow active:shadow-inner transition duration-100"
        >
          <svg
              class="stroke-current h-5 w-5 mr-2"
              fill="none"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          <span>{{ copied ? "Copied" : "Copy Details" }}</span>
        </button>
        <button
            type="button"
            @click="done"
            class="w-1/2 mx-2 flex items-center justify-center bg-blue-200 hover:bg-blue-300 text-secondary py-2 px-4 font-semibold rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
        >
          <span>Done</span>
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script>
const SEVERITY_LABELS = {0: "Normal", 1: "Low", 2: "Medium", 3: "High"};

export default {
  name: "ServiceTicketCreated",
  props: {
    ticket: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showDialog: false,
      copied: false,
      copiedTimer: null,
    };
  },
  computed: {
    createdOn() {
      if (!this.ticket || !this.ticket.created_at) return "";
      return this.$moment.unix(this.ticket.created_at).format("DD/MM/YYYY HH:mm");
    },
    followUp() {
      if (!this.ticket || !this.ticket.follow_up_at) return "";
      return this.$moment.unix(this.ticket.follow_up_at).format("DD/MM/YYYY");
    },
    severityLabel() {
      if (!this.ticket) return "";
      const label = SEVERITY_LABELS[parseInt(this.ticket.severity)];
      return label || "";
    },
    additionalContact() {
      if (!this.ticket) return "";
      return [this.ticket.contact_name_additional, this.ticket.contact_number_additional]
          .filter((part) => part)
          .join(" - ");
    },
    details() {
      if (!this.ticket) return [];
      return [
        {key: "customer", label: "Customer", value: this.ticket.contact_name},
        {key: "equipment", label: "Equipment", value: this.ticket.equipment_name},
        {key: "title", label: "Title", value: this.ticket.title},
        {key: "severity", label: "Severity", value: this.severityLabel},
        {key: "follow-up", label: "Follow Up", value: this.followUp},
        {key: "additional-contact", label: "Additional Contact", value: this.additionalContact},
      ].filter((detail) => detail.value);
    },
    summaryText() {
      if (!this.ticket) return "";
      const lines = [`Service Ticket ${this.ticket.ticket_number}`];
      if (this.ticket.warranty_number) {
        lines.push(`Warranty Number: ${this.ticket.warranty_number}`);
      }
      this.details.forEach((detail) => {
        lines.push(`${detail.label}: ${detail.value}`);
      });
      if (this.ticket.description) {
        lines.push(`Description: ${this.ticket.description}`);
      }
      if (this.ticket.items_for_return) {
        lines.push(`Items for Return: ${this.ticket.items_for_return_notes || "(No details added)"}`);
      }
      if (this.ticket.warranty_reason) {
        lines.push(`Reason Warranty Agreed: ${this.ticket.warranty_reason}`);
      }
      lines.push(`Created by: ${this.ticket.created_by}`);
      return lines.join("\n");
    },
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer);
  },
  methods: {
    open() {
      this.showDialog = true;
    },
    close() {
      this.showDialog = false;
    },
    done() {
      this.close();
      this.$emit("done");
    },
    async copyDetails() {
      const text = this.summaryText;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          this.copyWithTextarea(text);
        }
        this.markCopied();
      } catch (error) {
        this.copyWithTextarea(text);
        this.markCopied();
      }
    },
    copyWithTextarea(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    markCopied() {
      this.copied = true;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
  },
};
</script>
